import type { Chart, ChartDatum } from '../types'

// Trend X labels. The deterministic refresh path (result_to_chart → /api/run-spec)
// emits raw first-of-month ISO dates ("2026-01-01"), while a model-authored chat
// chart writes friendly month labels ("Jan 2026"). A pinned card and the same card
// after "Uppdatera med färska data" must read identically, so normalize a month
// series to "Mon ÅÅÅÅ". The series qualifies ONLY when EVERY label is a first-of-
// month date — a day series ("2026-05-14") never is, so day/week charts pass through.
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_START = /^(\d{4})-(\d{2})-01$/

// Trim leading/trailing all-zero buckets from a trend. A measure whose data starts
// part-way into the window (e.g. customer-id capture began mid-May → all earlier weeks
// are 0) otherwise renders as a long flat zero line then a spike, which reads as broken.
// Interior zeros are kept (a real dip is information); only the dead edges are removed,
// and only while ≥2 real points remain so it still reads as a trend.
export function trimZeroEdges(chart: Chart): Chart {
  const data = chart.data
  if (!data || data.length < 2) return chart
  const keys = chart.series && chart.series.length ? chart.series : ['value']
  const total = (row: ChartDatum) => keys.reduce((s, k) => s + (Number(row[k]) || 0), 0)
  let lo = 0
  let hi = data.length - 1
  while (lo < hi && total(data[lo]) === 0) lo++
  while (hi > lo && total(data[hi]) === 0) hi--
  if (lo === 0 && hi === data.length - 1) return chart
  const trimmed = data.slice(lo, hi + 1)
  return trimmed.length >= 2 ? { ...chart, data: trimmed } : chart
}

export function withTrendLabels(chart: Chart): Chart {
  const data = chart.data
  if (!data?.length) return chart
  // Explicit single-day metadata is not a whole month, even on the 1st.
  if (data.some((d) => typeof d.period_from === 'string' && d.period_from === d.period_to)) return chart
  const months = data.map((d) => MONTH_START.exec(String(d.name)))
  if (!months.every(Boolean)) return chart
  return {
    ...chart,
    data: data.map((d, i) => {
      const m = months[i]!
      return { ...d, name: `${MONTHS_EN[Number(m[2]) - 1]} ${m[1]}` }
    }),
  }
}
